import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import {
  Button, Group, Loader, Select, SimpleGrid, Stack, Text, Textarea, TextInput, Tooltip,
} from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import {
  IconAlertTriangle, IconArrowLeft, IconBan, IconClockHour4, IconKey, IconLock,
  IconMailForward, IconShieldCheck, IconShieldX, IconTrash, IconUser,
} from '@tabler/icons-react';
import { fetchList, createOne, deleteOne } from '@/shared/api/jsonapi/client';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { userFacingMessage } from '@/shared/api/errorMessage';
import { useCurrentUserId } from '@/shared/hooks/useCurrentUserId';
import { useCurrentTenantId } from '@/shared/hooks/useCurrentTenant';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MaeCard, MaeEyebrow, MaeField, MaePageHead, MaeShell, MaeTabs } from '@/shared/desktop/MaeShell';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { mw, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEffect, useState, type ReactNode } from 'react';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import * as userApi from '@/features/users/api';
import type { UserProfileRow, UserRow } from '@/features/users/api';
import { requestPasswordResetCode } from '@/features/auth/api';
import { normalizeBeIso } from '@/features/attendance/api';
import {
  UNASSIGNABLE_REASON, AUTO_MANAGED_REASON, isRoleAssignableToInternal, fetchAssignableRoleIds, isRoleSelectable, canAssign, roleLabel, isAutoManagedProfile,
} from '@/features/roles/api';
import { PageLoader } from '@/shared/components/LoadingState';
import { ImpersonateButton } from '@/features/auth/ImpersonateButton';
import { useExternalFields } from '@/features/admin/externalSync';
import { useExternalRowLock } from '@/features/admin/externalSyncLock';
import { ExternalOriginBadge, HandCorrectedFields } from '@/features/admin/ProvenienzaEsternaBadge';
import { EraseUserButton } from '@/features/users/EraseUserButton';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';

export const Route = createFileRoute('/_app/users/$userId')({ component: UserDetailPage });

/**
 * Scheda utente — anagrafica, ruoli, sicurezza.
 *
 * ── Perché è tutta MaeDesign condivisa ──
 *
 * La pagina aveva primitivi suoi (`SectionCard`, `PillButton`, una tab bar locale) nati prima
 * che quelli comuni esistessero: sembravano MaeDesign ma erano una seconda interpretazione, e
 * si vedeva accanto alle altre schede. Ora usa `MaeShell`/`MaePageHead`/`MaeCard`/`MaeField` e
 * l'unica `MaeTabs`: la scheda cambia insieme al resto dell'app invece di restare indietro.
 *
 * ── Responsive ──
 *
 * I campi stavano in `Group grow`, che **non va a capo**: su un telefono tre input affiancati
 * diventano tre colonne da un centimetro e il CAP si scrive alla cieca. Ora sono `SimpleGrid`
 * con colonne dichiarate per breakpoint, e su mobile è una colonna sola.
 *
 * ── La password ──
 *
 * Da qui non si *imposta* una password: si **manda il codice** (`account/password-reset-code`,
 * sei cifre valide dieci minuti) e la sceglie l'utente sulla pagina di recupero. È deliberato —
 * una password decisa dall'amministratore transita per la sua tastiera, per la chat in cui la
 * comunica, e resta valida finché l'utente non la cambia. Il BE non ha comunque un endpoint per
 * scriverla da qui, e non lo si è aggiunto per averne uno.
 *
 * Chi non ha **mai** avuto accesso è un caso diverso e ha il suo bottone: «Rinvia invito» rifà
 * la mail di benvenuto (`account/resend-invite`), che il reset non sostituisce.
 */

// ─── Chip locale: la pillola d'informazione accanto al nome ──────────────────
function Chip({ children, bg, color }: { children: ReactNode; bg: string; color: string }) {
  return <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: bg, color }}>{children}</span>;
}

/** Riquadro di sezione: card MaeDesign con l'occhiello in cima. */
function SectionCard({ label, children }: { label: string; children: ReactNode }) {
  const isMobile = useIsMobileShell();
  return (
    <MaeCard padding={isMobile ? 15 : 18}>
      <MaeEyebrow>{label}</MaeEyebrow>
      {children}
    </MaeCard>
  );
}

const USER_TABS = [
  { value: 'profile' as const, label: 'Profilo', icon: <IconUser size={15} /> },
  { value: 'roles' as const, label: 'Ruoli', icon: <IconShieldCheck size={15} /> },
  { value: 'security' as const, label: 'Sicurezza', icon: <IconLock size={15} /> },
];
type UserTab = typeof USER_TABS[number]['value'];

function initials(name: string, email?: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (email?.[0] ?? 'U').toUpperCase();
}

function UserDetailPage() {
  const { userId } = useParams({ from: '/_app/users/$userId' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const currentUserId = useCurrentUserId();
  const isMobile = useIsMobileShell();
  const isSuperAdmin = useIsSuperAdmin();
  const [tab, setTab] = useState<UserTab>('profile');
  // currentTenantId retained for future relationships writes; unused here now.
  void useCurrentTenantId();

  const userQ = useQuery({
    queryKey: ['user-with-profile', userId],
    queryFn: () => userApi.fetchUserWithProfile(userId),
    enabled: Boolean(userId),
  });
  const user = userQ.data?.user as UserRow | null | undefined;
  const profile = userQ.data?.profile as UserProfileRow | null | undefined;
  const isSelf = Boolean(currentUserId) && currentUserId === userId;

  /* Con il connettore MaeWay v8 l'account (email, nome, cognome dell'utente) può arrivare dal
     v8: lì si cambia, e con la policy `read-only` da qui non si elimina. Del profilo (`user-profiles`)
     il v8 governa nome e cognome — quelli che questa scheda mostra e modifica —, il resto resta nostro. */
  const external = useExternalFields('users', user);
  const externalProfile = useExternalFields('user-profiles', profile);
  const deleteLocked = useExternalRowLock('users')(user, 'L’utente');


  const deleteUser = useMutation({
    mutationFn: () => userApi.deleteUser(userId),
    onSuccess: () => {
      notifySuccess('Utente eliminato: non può più accedere, il suo storico resta.');
      void qc.invalidateQueries({ queryKey: ['desk-users'] });
      void navigate({ to: '/users' });
    },
    onError: (err) => notifyError(err, 'Eliminazione fallita'),
  });

  const resendInvite = useMutation({
    mutationFn: () => userApi.resendInvite(userId),
    onSuccess: () => notifySuccess('Invito reinviato: email in arrivo.'),
    onError: (err) => notifyError(err, 'Reinvio fallito'),
  });

  // ── Account form (User / AspNetUsers) ──
  const accountForm = useForm({
    mode: 'controlled',
    initialValues: { email: '', phoneNumber: '' },
  });

  // ── Profile form (UserProfile) ──
  const profileForm = useForm({
    mode: 'controlled',
    initialValues: {
      firstName: '', lastName: '', nickName: '',
      sex: '', taxId: '', birthDate: '', birthCity: '', birthProvince: '',
      fixedPhone: '', mobilePhone: '', contactEmail: '',
      residenceAddress: '', residenceHouseNumber: '', residenceCity: '',
      residenceProvince: '', residenceZIP: '', residenceState: '',
      occupation: '', description: '', userLang: 'it',
    },
  });

  useEffect(() => {
    if (user) accountForm.setValues({ email: user.email ?? '', phoneNumber: user.phoneNumber ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (profile) {
      profileForm.setValues({
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        nickName: profile.nickName ?? '',
        sex: profile.sex ?? '',
        taxId: profile.taxId ?? '',
        birthDate: profile.birthDate ? profile.birthDate.slice(0, 10) : '',
        birthCity: profile.birthCity ?? '',
        birthProvince: profile.birthProvince ?? '',
        fixedPhone: profile.fixedPhone ?? '',
        mobilePhone: profile.mobilePhone ?? '',
        contactEmail: profile.contactEmail ?? '',
        residenceAddress: profile.residenceAddress ?? '',
        residenceHouseNumber: profile.residenceHouseNumber ?? '',
        residenceCity: profile.residenceCity ?? '',
        residenceProvince: profile.residenceProvince ?? '',
        residenceZIP: profile.residenceZIP ?? '',
        residenceState: profile.residenceState ?? '',
        occupation: profile.occupation ?? '',
        description: profile.description ?? '',
        userLang: profile.userLang ?? 'it',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const saveAccount = useMutation({
    mutationFn: (v: typeof accountForm.values) => userApi.updateMe(userId, v),
    onSuccess: () => {
      notifySuccess('Account aggiornato');
      qc.invalidateQueries({ queryKey: ['user-with-profile', userId] });
    },
    onError: (err) => notifyError(err, 'Errore account'),
  });

  const saveProfile = useMutation({
    mutationFn: (v: typeof profileForm.values) => {
      const attrs = { ...v, birthDate: v.birthDate || undefined };
      if (profile?.id) return userApi.updateUserProfile(profile.id, attrs);
      // Nessun UserProfile esistente: lo creiamo con la relazione su user.
      return userApi.createUserProfile(userId, { ...attrs, contactEmail: attrs.contactEmail || user?.email });
    },
    onSuccess: () => {
      notifySuccess('Profilo aggiornato');
      qc.invalidateQueries({ queryKey: ['user-with-profile', userId] });
    },
    onError: (err) => notifyError(err, 'Errore profilo'),
  });

  const displayName = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim()
    || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
    || user?.email
    || '';

  const goBack = (
    <button
      onClick={() => void navigate({ to: '/users' })}
      className="mw-pressable"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: 0, background: 'transparent', cursor: 'pointer', color: mw.accentText, fontSize: 'var(--fs-compact)', fontWeight: 600, fontFamily: 'inherit', padding: 0, marginBottom: 14 }}
    >
      <IconArrowLeft size={15} /> Torna a Utenti
    </button>
  );

  const shell = (content: ReactNode) => (
    isMobile
      ? <div style={{ padding: '16px 16px 28px' }}>{goBack}{content}</div>
      : <MaeShell>{goBack}{content}</MaeShell>
  );

  /* Un 500 sulla lettura non deve assomigliare a «utente inesistente»: sono due cose
     diverse, e solo una delle due si risolve riprovando. */
  if (userQ.isError) {
    return shell(
      <QueryErrorAlert
        error={userQ.error}
        title="Scheda utente non caricata"
        onRetry={() => void userQ.refetch()}
      />,
    );
  }
  if (userQ.isLoading) return <PageLoader label="Carico l'utente…" />;
  if (!user) {
    return shell(
      <MaeAdvice
        tone="warning"
        title="Utente non trovato"
        action={{ label: 'Torna a Utenti', onClick: () => void navigate({ to: '/users' }) }}
      >
        Questa scheda non esiste più, oppure appartiene a un’altra azienda e da qui non è
        leggibile. Se l’hai appena eliminata è normale.
      </MaeAdvice>,
    );
  }

  const openDeleteConfirm = () => {
    modals.openConfirmModal({
      title: 'Elimina utente',
      children: (
        <Stack gap="xs">
          <Text size="sm">
            Confermi l&apos;eliminazione di <strong>{displayName}</strong> ({user.email})?
          </Text>
          {/* Quello che fa davvero `DELETE /api/users/{id}` (BE `DeleteUserLite`): non cancella la
              riga, la **anonimizza** — via aziende, ruoli, dispositivi, passkey, codici e cronologia
              degli accessi; e-mail e nome d'accesso resi irriconoscibili, il nome marcato «Erased».
              Il lavoro registrato a suo nome resta, perché altri dati lo citano. Fino al 26-10-2026
              la finestra prometteva di togliere «storico associati», che è il contrario. */}
          <MaeAdvice tone="danger" title="Azione irreversibile" selfRemedy="Se vuoi solo togliergli l’accesso, usa «Banna utente» nella scheda Sicurezza: si può revocare.">
            L&apos;utente non potrà più accedere: perde le aziende, i profili di permesso, i
            dispositivi e le passkey, e il suo indirizzo e-mail viene reso anonimo. Quello che ha
            fatto resta — scheda del dipendente, task, ore, ticket e il resto dello storico — e la
            scheda del dipendente tiene nome e recapiti.
            {isSuperAdmin ? ' Per togliere anche i dati personali usa «Cancella definitivamente».' : ''}
          </MaeAdvice>
        </Stack>
      ),
      labels: { confirm: 'Elimina utente', cancel: 'Annulla' },
      confirmProps: { color: 'red', leftSection: <IconAlertTriangle size={15} /> },
      onConfirm: () => deleteUser.mutate(),
    });
  };

  /* Due ragioni per non poter eliminare, e la prima vince: sé stessi, poi la provenienza esterna. */
  const deleteBlockedReason = isSelf ? 'Non puoi eliminare il tuo stesso account' : deleteLocked;
  const deleteBtn = (
    <Button color="red" leftSection={<IconTrash size={16} />} loading={deleteUser.isPending}
      disabled={Boolean(deleteBlockedReason)} onClick={openDeleteConfirm} fullWidth={isMobile}>
      Elimina
    </Button>
  );

  const actions = (
    <>
      {userApi.hasNeverAccessed(user) && (
        <Button
          variant="light"
          leftSection={<IconMailForward size={16} />}
          loading={resendInvite.isPending}
          fullWidth={isMobile}
          onClick={() => {
            modals.openConfirmModal({
              title: 'Rinvia invito',
              children: (
                <Text size="sm">
                  Inviare una nuova email «Imposta la tua password» a <strong>{displayName}</strong> ({user.email})?
                  Il link precedente, se non ancora usato, verrà invalidato.
                </Text>
              ),
              labels: { confirm: 'Rinvia invito', cancel: 'Annulla' },
              onConfirm: () => resendInvite.mutate(),
            });
          }}
        >
          Rinvia invito
        </Button>
      )}
      <ImpersonateButton email={user?.email} displayName={displayName} fullWidth={isMobile} />
      {/* Solo super-admin: la rotta `users/{id}/hard` è `NeededRoleLevel0`, a chiunque altro risponde
          403. Stesse ragioni di blocco di «Elimina». */}
      {isSuperAdmin && (
        deleteBlockedReason ? (
          <Tooltip label={deleteBlockedReason} multiline w={280} withArrow>
            <span style={{ width: isMobile ? '100%' : undefined }}>
              <EraseUserButton userId={userId} email={user.email ?? ''} displayName={displayName} disabled fullWidth={isMobile} onErased={() => undefined} />
            </span>
          </Tooltip>
        ) : (
          <EraseUserButton userId={userId} email={user.email ?? ''} displayName={displayName} fullWidth={isMobile}
            onErased={() => void navigate({ to: '/users' })} />
        )
      )}
      {deleteBlockedReason ? (
        <Tooltip label={deleteBlockedReason} multiline w={280} withArrow>
          <span style={{ width: isMobile ? '100%' : undefined }}>{deleteBtn}</span>
        </Tooltip>
      ) : deleteBtn}
    </>
  );

  const contenuto = (
    <>
      <MaePageHead
        title={displayName}
        /* Senza anagrafica il titolo **e'** l'email: ripeterla sotto la scriverebbe due volte. */
        sub={displayName === user.email ? undefined : user.email}
        icon={<span style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, letterSpacing: '0.02em' }}>{initials(displayName, user.email)}</span>}
        actions={actions}
      />

      <Group gap={8} mb={16} wrap="wrap" align="center">
        <ExternalOriginBadge row={user} />
        {user.emailConfirmed && <Chip bg={mw.successBg} color={mw.success}>Email verificata</Chip>}
        {user.lockoutEnabled && <Chip bg={mw.dangerBg} color={mw.danger}>Bloccato</Chip>}
        {!profile && <Chip bg={mw.warningBg} color={mw.warning}>Profilo mancante</Chip>}
        {userApi.hasNeverAccessed(user) && <Chip bg={mw.warningBg} color={mw.warning}>Invito non ancora accettato</Chip>}
      </Group>

      <MaeTabs value={tab} onChange={setTab} tabs={USER_TABS} mb={16} />

      {/* Solo la scheda attiva monta: quelle chiuse non fanno le loro letture. */}
      {tab === 'profile' && (
        <Stack gap={14}>
          <SectionCard label="Account">
            <form onSubmit={accountForm.onSubmit((v) => { void saveAccount.mutate(v); })}>
              <Stack gap={14}>
                <HandCorrectedFields row={user} resourceType="users" />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={14}>
                  <MaeField label="Email" hint="Si cambia dal profilo dell’utente, non da qui.">
                    <TextInput disabled {...accountForm.getInputProps('email')} {...external('email')} />
                  </MaeField>
                  <MaeField label="Telefono account">
                    <TextInput {...accountForm.getInputProps('phoneNumber')} />
                  </MaeField>
                </SimpleGrid>
                <Group justify="flex-end">
                  <Button type="submit" variant="light" loading={saveAccount.isPending} fullWidth={isMobile}>
                    Salva account
                  </Button>
                </Group>
              </Stack>
            </form>
          </SectionCard>

          <SectionCard label="Anagrafica">
            {!profile && (
              <MaeAdvice
                tone="warning"
                title="Questo utente non ha una scheda anagrafica"
                selfRemedy="Compila i campi qui sotto e salva: la scheda viene creata al primo salvataggio."
              >
                Senza anagrafica l’utente resta un account con la sola email: nome, recapiti e
                residenza non sono disponibili al resto dell’applicativo.
              </MaeAdvice>
            )}
            <form onSubmit={profileForm.onSubmit((v) => { void saveProfile.mutate(v); })}>
              <Stack gap={14} mt={profile ? 0 : 14}>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={14}>
                  <MaeField label="Nome"><TextInput {...profileForm.getInputProps('firstName')} {...externalProfile('firstName')} /></MaeField>
                  <MaeField label="Cognome"><TextInput {...profileForm.getInputProps('lastName')} {...externalProfile('lastName')} /></MaeField>
                  <MaeField label="Nickname"><TextInput {...profileForm.getInputProps('nickName')} /></MaeField>
                  <MaeField label="Sesso">
                    <Select
                      data={[{ value: 'M', label: 'M' }, { value: 'F', label: 'F' }, { value: 'X', label: 'Altro' }]}
                      clearable
                      {...profileForm.getInputProps('sex')}
                    />
                  </MaeField>
                  <MaeField label="Codice fiscale"><TextInput {...profileForm.getInputProps('taxId')} /></MaeField>
                  <MaeField label="Data di nascita"><TextInput type="date" {...profileForm.getInputProps('birthDate')} /></MaeField>
                  <MaeField label="Città di nascita"><TextInput {...profileForm.getInputProps('birthCity')} /></MaeField>
                  <MaeField label="Provincia di nascita"><TextInput {...profileForm.getInputProps('birthProvince')} /></MaeField>
                </SimpleGrid>

                <MaeEyebrow mb={0}>Recapiti</MaeEyebrow>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={14}>
                  <MaeField label="Telefono fisso"><TextInput {...profileForm.getInputProps('fixedPhone')} /></MaeField>
                  <MaeField label="Cellulare"><TextInput {...profileForm.getInputProps('mobilePhone')} /></MaeField>
                </SimpleGrid>
                <MaeField label="Email di contatto"><TextInput {...profileForm.getInputProps('contactEmail')} /></MaeField>

                <MaeEyebrow mb={0}>Residenza</MaeEyebrow>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={14}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <MaeField label="Indirizzo"><TextInput {...profileForm.getInputProps('residenceAddress')} /></MaeField>
                  </div>
                  <MaeField label="Civico"><TextInput {...profileForm.getInputProps('residenceHouseNumber')} /></MaeField>
                  <MaeField label="Città"><TextInput {...profileForm.getInputProps('residenceCity')} /></MaeField>
                  <MaeField label="Provincia"><TextInput {...profileForm.getInputProps('residenceProvince')} /></MaeField>
                  <MaeField label="CAP"><TextInput {...profileForm.getInputProps('residenceZIP')} /></MaeField>
                </SimpleGrid>
                <MaeField label="Stato"><TextInput {...profileForm.getInputProps('residenceState')} /></MaeField>

                <MaeEyebrow mb={0}>Altro</MaeEyebrow>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={14}>
                  <MaeField label="Occupazione"><TextInput {...profileForm.getInputProps('occupation')} /></MaeField>
                  <MaeField label="Lingua">
                    <Select
                      data={[{ value: 'it', label: 'Italiano' }, { value: 'en', label: 'English' }]}
                      {...profileForm.getInputProps('userLang')}
                    />
                  </MaeField>
                </SimpleGrid>
                <MaeField label="Note"><Textarea autosize minRows={2} {...profileForm.getInputProps('description')} /></MaeField>

                <Group justify="flex-end">
                  <Button type="submit" loading={saveProfile.isPending} fullWidth={isMobile}>
                    {profile ? 'Salva profilo' : 'Crea profilo'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </SectionCard>
        </Stack>
      )}

      {tab === 'roles' && <UserRolesPanel userId={userId} />}
      {tab === 'security' && <UserSecurityPanel userId={userId} user={user} displayName={displayName} />}
    </>
  );

  return shell(contenuto);
}

function errorMessage(err: unknown): string {
  return userFacingMessage(err, 'Errore');
}

interface RoleListItem { type: 'roles'; id: string; name?: string; displayName?: string | null; typology?: string; normalizedName?: string; isFromMaster?: boolean; [k: string]: unknown; }
interface UserRoleItem { type: 'user-roles'; id: string; tenantId?: string; role?: { id: string }; user?: { id: string }; [k: string]: unknown; }

function UserRolesPanel({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const isMobile = useIsMobileShell();
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const rolesQ = useQuery({
    queryKey: ['all-roles'],
    queryFn: () => fetchList<RoleListItem>('roles', { sort: ['name'], page: { size: 100 } }),
  });

  const userRolesQ = useQuery({
    queryKey: ['user-roles', userId],
    queryFn: () => fetchList<UserRoleItem>('user-roles', {
      filter: { 'user.id': { eq: userId } },
      include: ['role'],
      page: { size: 100 },
    }),
    enabled: Boolean(userId),
  });

  /* Quali ruoli il **server** mi lascia assegnare. Prima questa schermata li offriva tutti e
     scopriva il divieto dal 403 dopo il clic: la regola di escalation è del BE e la teniamo
     dalla sua bocca invece di riprodurla. Se la chiamata cade, `null` = si offre tutto e il
     muro resta il salvataggio (vedi `fetchAssignableRoleIds`). */
  const assignableQ = useQuery({
    queryKey: ['assignable-roles'],
    staleTime: 5 * 60_000,
    queryFn: fetchAssignableRoleIds,
  });

  const allRoles = rolesQ.data?.data ?? [];
  const assigned = userRolesQ.data?.data ?? [];
  const assignedRoleIds = new Set(assigned.map((ur) => ur.role?.id).filter(Boolean) as string[]);
  // Decisione FE D.2: i profili `erp` senza `displayName` sono porting inerti — non si propongono.
  // Restano visibili se già assegnati (sopra), così un'assegnazione storica non diventa invisibile.
  /* Fuori anche i profili che dà il sistema (`isAutoManagedProfile`): dati a mano, alla prima
     transizione il sistema li toglierebbe. */
  const availableRoles = allRoles.filter((r) => !assignedRoleIds.has(r.id) && isRoleSelectable(r) && !isAutoManagedProfile(r)
    /* Il portale non si dà a un interno: il sistema rifiuterebbe (422). */
    && isRoleAssignableToInternal(r));
  /* Spenti e non nascosti: un ruolo che non trovi sembra non esistere, uno spento con il motivo
     dice a chi chiedere. È la regola di `MaeGate` applicata a una tendina. */
  const roleOptions = availableRoles.map((r) => {
    const allowed = canAssign(assignableQ.data ?? null, r.id);
    return {
      value: r.id,
      label: allowed ? roleLabel(r) : `${roleLabel(r)} — non assegnabile da te`,
      disabled: !allowed,
    };
  });
  const unassignable = roleOptions.filter((o) => o.disabled).length;
  /** Perché questo profilo non si toglie da qui, o `null`. */
  const removeBlocked = (role?: { id: string } | null): string | null =>
    (role && !canAssign(assignableQ.data ?? null, role.id) ? UNASSIGNABLE_REASON : null);

  const assign = useMutation({
    mutationFn: (roleId: string) => createOne<UserRoleItem>('user-roles', {
      relationships: {
        user: { data: { type: 'users', id: userId } },
        role: { data: { type: 'roles', id: roleId } },
      },
    }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Ruolo assegnato' });
      setSelectedRoleId(null);
      qc.invalidateQueries({ queryKey: ['user-roles', userId] });
    },
    // BE (post-v1.9): l'assegnazione ora funziona anche per non-superadmin con i
    // claim sufficienti. Un 403 residuo = escalation di privilegi rifiutata.
    onError: (err) => {
      /* Un 403 col suo codice (dal BE v1.266.0: «solo dal Master», «permessi che non hai») dice da sé
         il motivo; quello generico resta per un 403 senza codice. */
      const is403 = err instanceof JsonApiHttpError && err.status === 403 && !err.getApiCode();
      notifications.show({
        color: 'red',
        title: is403 ? 'Privilegi insufficienti' : 'Errore',
        message: is403 ? UNASSIGNABLE_REASON : errorMessage(err),
      });
    },
  });

  const unassign = useMutation({
    mutationFn: (userRoleId: string) => deleteOne('user-roles', userRoleId),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Ruolo rimosso' });
      qc.invalidateQueries({ queryKey: ['user-roles', userId] });
    },
    onError: (err) => notifications.show({ color: 'red', title: 'Errore', message: errorMessage(err) }),
  });

  return (
    <Stack gap={14}>
      <SectionCard label="Ruoli assegnati">
        {userRolesQ.isError ? <QueryErrorAlert error={userRolesQ.error} onRetry={() => { void userRolesQ.refetch(); }} /> : userRolesQ.isLoading ? <Loader /> : assigned.length === 0 ? (
          <Text size="sm" style={{ color: mw.ink300 }}>Nessun ruolo assegnato.</Text>
        ) : (
          <Stack gap="xs">
            {assigned.map((ur) => {
              const role = allRoles.find((r) => r.id === ur.role?.id);
              return (
                <Group key={ur.id} justify="space-between" wrap="nowrap" gap={10}>
                  <Group gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
                    <Chip bg={mw.accentTint} color={mw.accentText}>{role ? roleLabel(role) : ur.role?.id ?? '—'}</Chip>
                    {isAutoManagedProfile(role)
                      ? <Tooltip label={AUTO_MANAGED_REASON} withArrow multiline w={280}><span><Chip bg={mw.surface2} color={mw.ink500}>Automatico</Chip></span></Tooltip>
                      : role?.isFromMaster && <Chip bg={mw.surface2} color={mw.ink500}>Sistema</Chip>}
                    {role?.displayName?.trim() && <Text size="xs" style={{ color: mw.ink300, ...MW_CODE_STYLE }}>{role.name}</Text>}
                  </Group>
                  {!isAutoManagedProfile(role) && <button
                    /* Togliere chiede conferma, e segue la regola del dare: chi non potrebbe dare
                       questo profilo non lo toglie (il sistema in cancellazione oggi non controlla,
                       task 2030 §7). */
                    onClick={() => modals.openConfirmModal({
                      title: 'Togliere il ruolo?',
                      children: <Text size="sm">L’utente perde il ruolo <strong>{role ? roleLabel(role) : '—'}</strong> e i permessi che porta, a meno che non li abbia da un altro ruolo.</Text>,
                      labels: { confirm: 'Togli', cancel: 'Annulla' },
                      confirmProps: { color: 'red' },
                      onConfirm: () => unassign.mutate(ur.id),
                    })}
                    title={removeBlocked(role) ?? 'Togli il ruolo'}
                    aria-label="Rimuovi ruolo"
                    className="mw-pressable"
                    disabled={unassign.isPending || Boolean(removeBlocked(role))}
                    style={{ border: 0, background: 'transparent', cursor: unassign.isPending ? 'progress' : 'pointer', color: mw.danger, display: 'flex', alignItems: 'center', padding: 6, borderRadius: 8, opacity: unassign.isPending ? 0.5 : 1, flexShrink: 0 }}
                  >
                    <IconTrash size={16} />
                  </button>}
                </Group>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      <SectionCard label="Assegna nuovo ruolo">
        {rolesQ.isError ? (
          <QueryErrorAlert error={rolesQ.error} title="Elenco ruoli non caricato" onRetry={() => { void rolesQ.refetch(); }} />
        ) : (
          <>
            {/* Su mobile la select prende la riga e il bottone quella sotto: affiancati
                diventerebbero due controlli da un centimetro. */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
              <Select
                placeholder="Seleziona ruolo…"
                data={roleOptions}
                value={selectedRoleId}
                onChange={setSelectedRoleId}
                searchable
                disabled={availableRoles.length === 0}
                aria-label="Ruolo da assegnare"
              />
              <Button
                variant="light"
                disabled={!selectedRoleId}
                loading={assign.isPending}
                onClick={() => selectedRoleId && assign.mutate(selectedRoleId)}
                fullWidth={isMobile}
                style={{ alignSelf: 'start', justifySelf: isMobile ? 'stretch' : 'start' }}
              >
                Assegna
              </Button>
            </SimpleGrid>
            {availableRoles.length === 0 && allRoles.length > 0 && (
              <Text size="xs" style={{ color: mw.ink300 }} mt="xs">Tutti i ruoli disponibili sono già assegnati.</Text>
            )}
            <Text size="xs" style={{ color: mw.ink300 }} mt="xs">
              «User», «Dipendente» e «Responsabile diretto» non sono nell’elenco: li assegna e li toglie il sistema.
            </Text>
            {unassignable > 0 && (
              <Text size="xs" style={{ color: mw.ink300 }} mt="xs">
                {unassignable === 1 ? 'Un ruolo è spento' : `${unassignable} ruoli sono spenti`} nell&apos;elenco: {UNASSIGNABLE_REASON}
              </Text>
            )}
          </>
        )}
      </SectionCard>
    </Stack>
  );
}

interface BannedUserItem { type: 'banned-users'; id: string; userId?: string; tenantId?: string; lockEnd?: string | null; lockActive?: boolean; [k: string]: unknown; }

function UserSecurityPanel({ userId, user, displayName }: {
  userId: string; user: UserRow | null | undefined; displayName: string;
}) {
  const qc = useQueryClient();
  const isMobile = useIsMobileShell();

  const banQ = useQuery({
    queryKey: ['banned-users', userId],
    queryFn: () => fetchList<BannedUserItem>('banned-users', {
      filter: { 'user.id': { eq: userId } },
      page: { size: 10 },
    }),
    enabled: Boolean(userId),
  });
  const activeBan = (banQ.data?.data ?? []).find((b) => b.lockActive === true);

  const ban = useMutation({
    mutationFn: () => createOne<BannedUserItem>('banned-users', {
      attributes: {
        lockStart: new Date().toISOString(),
        lockEnd: new Date(Date.now() + 100 * 365 * 24 * 3600 * 1000).toISOString(),
        lockActive: true,
        reason: 'Banned da admin',
      } as Record<string, unknown>,
      relationships: {
        user: { data: { type: 'users', id: userId } },
      },
    }),
    onSuccess: () => {
      notifications.show({ color: 'orange', message: 'Utente bannato' });
      qc.invalidateQueries({ queryKey: ['banned-users', userId] });
    },
    onError: (err) => notifications.show({ color: 'red', title: 'Errore', message: errorMessage(err) }),
  });

  const unban = useMutation({
    mutationFn: (id: string) => deleteOne('banned-users', id),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Ban rimosso' });
      qc.invalidateQueries({ queryKey: ['banned-users', userId] });
    },
    onError: (err) => notifications.show({ color: 'red', title: 'Errore', message: errorMessage(err) }),
  });

  return (
    <Stack gap={14}>
      <PasswordCard user={user} displayName={displayName} />

      <SectionCard label="Accesso all’applicativo">
        <Group justify="space-between" wrap="wrap" gap={10} mb={12}>
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>
            {activeBan
              ? 'L’account è bloccato: le credenziali restano valide ma il login viene rifiutato.'
              : 'L’account può accedere normalmente.'}
          </Text>
          {activeBan ? <Chip bg={mw.dangerBg} color={mw.danger}>Bannato</Chip> : <Chip bg={mw.successBg} color={mw.success}>Attivo</Chip>}
        </Group>
        {banQ.isError ? (
          <QueryErrorAlert error={banQ.error} title="Stato del ban non leggibile" onRetry={() => { void banQ.refetch(); }} />
        ) : activeBan ? (
          <Stack gap={12}>
            <MaeAdvice
              tone="warning"
              icon={IconShieldX}
              title={`Bannato ${activeBan.lockEnd ? `fino al ${new Date(activeBan.lockEnd).toLocaleDateString('it-IT')}` : 'a tempo indeterminato'}`}
              action={{ label: unban.isPending ? 'Rimuovo…' : 'Rimuovi il ban', onClick: () => unban.mutate(activeBan.id) }}
            >
              Finché il ban è attivo l’utente non può entrare, da nessun dispositivo.
            </MaeAdvice>
          </Stack>
        ) : (
          <Group justify="flex-end">
            <Button
              color="red"
              variant="light"
              leftSection={<IconBan size={16} />}
              fullWidth={isMobile}
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Banna utente',
                  children: (
                    <Text size="sm">
                      Bloccare l’accesso di <strong>{displayName}</strong>? Le credenziali restano
                      valide, ma il login viene rifiutato finché non rimuovi il ban.
                    </Text>
                  ),
                  labels: { confirm: 'Banna', cancel: 'Annulla' },
                  confirmProps: { color: 'red' },
                  onConfirm: () => ban.mutate(),
                });
              }}
              loading={ban.isPending}
            >
              Banna utente
            </Button>
          </Group>
        )}
      </SectionCard>
    </Stack>
  );
}

/**
 * **Password** — si manda il codice, non si sceglie la password.
 *
 * `POST account/password-reset-code` genera sei cifre valide dieci minuti, spegne i codici
 * precedenti dello stesso utente e le manda alla sua email; l'utente le digita su
 * `/password-reset` e sceglie lì la nuova password. Da qui non passa nessuna password, il che
 * è il punto: una decisa dall'amministratore vive nella chat in cui gliela comunica.
 *
 * Chi non ha mai avuto accesso non è in questo caso — non ha una password da reimpostare, ha
 * un invito da rifare — e la scheda glielo dice invece di mandargli un codice che si
 * sovrapporrebbe alla mail di benvenuto.
 */
function PasswordCard({ user, displayName }: { user: UserRow | null | undefined; displayName: string }) {
  const isMobile = useIsMobileShell();
  const neverEntered = user ? userApi.hasNeverAccessed(user) : false;
  /* Gli istanti del BE arrivano senza `Z`: letti come ora locale slittano di due ore, e a
     ridosso di mezzanotte il giorno mostrato sarebbe quello sbagliato. */
  const lastChange = user?.passwordLastChange && !neverEntered
    ? dayjs(normalizeBeIso(user.passwordLastChange)).format('D MMMM YYYY')
    : null;

  const sendCode = useMutation({
    mutationFn: () => requestPasswordResetCode(user?.email ?? ''),
    /* La notifica nomina la **pagina**, non solo l'esito: la mail dice «inseriscilo nella pagina
       di recupero» senza dire quale e senza portare un link, quindi l'unico che può spiegarlo è
       chi ha appena premuto il pulsante. */
    onSuccess: () => notifySuccess(
      `Codice inviato a ${user?.email}: sei cifre valide dieci minuti. Digli di aprire `
      + '«Recupera password» dal login e di scegliere «Ho già un codice».',
    ),
    onError: (err) => notifyError(err, 'Invio del codice non riuscito'),
  });

  return (
    <SectionCard label="Password">
      <Group gap={12} wrap="nowrap" align="flex-start" mb={12}>
        <span style={{
          width: 38, height: 38, borderRadius: 11, flexShrink: 0, background: mw.surface2,
          color: mw.ink300, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <IconKey size={19} />
        </span>
        <div style={{ minWidth: 0 }}>
          <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: mw.ink900 }}>
            {neverEntered
              ? 'Non ha ancora impostato una password'
              : lastChange ? `Ultimo cambio: ${lastChange}` : 'Password impostata'}
          </Text>
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 3, lineHeight: 1.5 }}>
            La password non si legge e non si scrive da qui, nemmeno da amministratore: si manda
            all’utente un codice di sei cifre, e la nuova password la sceglie lui.
          </Text>
          {!neverEntered && (
            <Group gap={6} mt={6} wrap="nowrap" style={{ color: mw.ink300 }}>
              <IconClockHour4 size={14} />
              <Text style={{ fontSize: 'var(--fs-compact)' }}>Il codice vale dieci minuti e annulla i precedenti.</Text>
            </Group>
          )}
        </div>
      </Group>

      {neverEntered ? (
        <MaeAdvice
          tone="info"
          title="Qui serve l’invito, non il reset"
          selfRemedy="Usa «Rinvia invito» in cima alla scheda: rifà la mail di benvenuto con un link valido."
        >
          Non ha mai completato il primo accesso, quindi non ha una password da reimpostare. Un
          codice di reset gli arriverebbe accanto all’invito, e sarebbero due mail che chiedono
          la stessa cosa in due modi diversi.
        </MaeAdvice>
      ) : (
        <Group justify="flex-end">
          <Button
            variant="light"
            leftSection={<IconKey size={16} />}
            loading={sendCode.isPending}
            disabled={!user?.email}
            fullWidth={isMobile}
            onClick={() => {
              modals.openConfirmModal({
                title: 'Reimposta la password',
                children: (
                  <Stack gap="xs">
                    <Text size="sm">
                      Inviare a <strong>{displayName}</strong> ({user?.email}) un codice di sei
                      cifre per scegliere una nuova password?
                    </Text>
                    {/* ⚠️ La strada va detta per intero. La mail non porta nessun link e dice solo
                        «inseriscilo nella pagina di recupero»; e su quella pagina il primo passo
                        **genera un codice nuovo**, spegnendo questo. Senza «Ho già un codice» chi
                        riceve il messaggio non ha modo di usarlo. */}
                    <Text size="sm" c="dimmed">
                      Vale dieci minuti e annulla eventuali codici precedenti. Per usarlo deve
                      aprire «Recupera password» dal login e scegliere <strong>«Ho già un
                      codice»</strong>: chiedendone uno nuovo, questo si spegne. La password
                      attuale resta valida finché non ne imposta una nuova.
                    </Text>
                  </Stack>
                ),
                labels: { confirm: 'Invia il codice', cancel: 'Annulla' },
                onConfirm: () => sendCode.mutate(),
              });
            }}
          >
            Invia codice di reimpostazione
          </Button>
        </Group>
      )}
    </SectionCard>
  );
}
